import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import Header from '@/components/Header';
import { toast } from 'sonner';
import { CheckCircle2, ArrowRight, BarChart3, Phone, MessageSquare, AlertCircle, Loader2, Tag, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { API } from '@/lib/api';
import { httpRequest, HttpError } from '@/lib/http';
import { PLAN_OPTIONS, planPricingFor, readAndClearPendingPlan } from '@/lib/plans';
import PostPaymentResiliationDialog from '@/components/PostPaymentResiliationDialog';

/** Mensualité gagnée estimée (champ API ou total_savings / mois restants). */
function estimatedMonthlySavingEur(result) {
  if (!result || typeof result !== 'object') return null;
  const sa = result.savings_analysis || {};
  const ms = Number(sa.monthly_savings);
  if (Number.isFinite(ms) && ms > 0) return ms;
  const total = Number(sa.total_savings);
  const rem = Number(result.contract_details?.remaining_months);
  if (Number.isFinite(total) && total > 0 && Number.isFinite(rem) && rem > 0) {
    return Math.round((total / rem) * 100) / 100;
  }
  return null;
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [analyses, setAnalyses] = useState([]);
  const [pendingClaim, setPendingClaim] = useState(null);
  const [claiming, setClaiming] = useState(false);
  const [phone, setPhone] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [stripeCheckoutEnabled, setStripeCheckoutEnabled] = useState(false);
  const [stripeMinEur, setStripeMinEur] = useState(0.5);
  const stripeReturnHandledRef = useRef(false);
  const [postPaymentResiliationOpen, setPostPaymentResiliationOpen] = useState(false);
  const [promoInput, setPromoInput] = useState('');
  const [promoValidating, setPromoValidating] = useState(false);
  const [promoApplied, setPromoApplied] = useState(null);
  const [selectedPlan, setSelectedPlan] = useState('essentiel');

  const loadData = useCallback(async () => {
    try {
      const [statsData, analysesData] = await Promise.all([
        httpRequest(`${API}/dashboard/stats`),
        httpRequest(`${API}/analyses`),
      ]);
      setStats(statsData);
      setAnalyses(analysesData);
    } catch (err) {
      console.error('Failed to load data', err);
    }
  }, []);

  // Auto-claim pending analysis from landing page
  useEffect(() => {
    const token = localStorage.getItem('pending_analysis_token');
    if (token && user) {
      setClaiming(true);
      httpRequest(`${API}/analyses/claim`, {
        method: 'POST',
        body: { analysis_token: token },
      })
        .then(async () => {
          localStorage.removeItem('pending_analysis_token');
          toast.success('Analyse associee a votre compte !');
          // Fetch the claimed analysis
          const data = await httpRequest(`${API}/analyses/public/${token}`);
          setPendingClaim({ ...data, analysis_token: token });
          const wantsPaymentStep =
            sessionStorage.getItem('leaseoptimizer_open_payment_after_claim') === '1';
          if (
            wantsPaymentStep &&
            data.has_savings &&
            (data.payment_status === 'pending' || data.payment_status === 'none')
          ) {
            sessionStorage.removeItem('leaseoptimizer_open_payment_after_claim');
            toast.info(
              'Étape suivante : choisissez votre formule (Essentiel ou Sérénité), renseignez vos coordonnées puis validez le paiement ou la confirmation pour lancer la prise en charge.',
              { duration: 8000 },
            );
          } else if (wantsPaymentStep) {
            sessionStorage.removeItem('leaseoptimizer_open_payment_after_claim');
          }
          loadData();
        })
        .catch((err) => {
          console.error('Claim failed', err);
          localStorage.removeItem('pending_analysis_token');
          sessionStorage.removeItem('leaseoptimizer_open_payment_after_claim');
          loadData();
        })
        .finally(() => setClaiming(false));
    } else {
      loadData();
    }
  }, [user, loadData]);

  useEffect(() => {
    if (!pendingClaim?.analysis_token) return;
    const stored = readAndClearPendingPlan();
    setSelectedPlan(stored || 'essentiel');
  }, [pendingClaim?.analysis_token]);

  useEffect(() => {
    httpRequest(`${API}/payments/config`)
      .then((cfg) => {
        setStripeCheckoutEnabled(!!cfg?.stripe_checkout_enabled);
        if (typeof cfg?.stripe_min_amount_eur === 'number') {
          setStripeMinEur(cfg.stripe_min_amount_eur);
        }
      })
      .catch(() => setStripeCheckoutEnabled(false));
  }, []);

  useEffect(() => {
    if (!user) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('payment') === 'cancelled') {
      toast.info('Paiement annulé');
      window.history.replaceState({}, '', '/dashboard');
    }
  }, [user]);

  useEffect(() => {
    if (!user || stripeReturnHandledRef.current) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('payment') !== 'success') return;
    const sessionId = params.get('session_id');
    if (!sessionId) return;
    stripeReturnHandledRef.current = true;
    (async () => {
      try {
        const r = await httpRequest(
          `${API}/payments/session-status?session_id=${encodeURIComponent(sessionId)}`,
        );
        if (r.paid && r.ok !== false) {
          toast.success('Paiement confirmé — votre demande est enregistrée.');
          setPendingClaim(null);
          setPhone('');
          setMessage('');
          setPostPaymentResiliationOpen(true);
        } else {
          toast.info('Paiement en attente de confirmation. Actualisez dans un instant.');
        }
      } catch (err) {
        toast.error(err instanceof HttpError ? err.message : 'Vérification du paiement impossible');
      } finally {
        window.history.replaceState({}, '', '/dashboard');
        loadData();
      }
    })();
  }, [user, loadData]);

  const handleValidatePromo = async () => {
    const code = promoInput.trim().toUpperCase()
    if (!code) return
    setPromoValidating(true)
    try {
      const result = await httpRequest(`${API}/promo/validate`, {
        method: 'POST',
        body: { code },
      })
      if (result.valid) {
        setPromoApplied(result)
        toast.success(
          result.remaining != null
            ? `Code applique — ${result.remaining} utilisation${result.remaining > 1 ? 's' : ''} restante${result.remaining > 1 ? 's' : ''}`
            : 'Code promo applique',
        )
      } else {
        toast.error(result.reason || 'Code promo invalide')
        setPromoApplied(null)
      }
    } catch (err) {
      toast.error(err instanceof HttpError ? err.message : 'Erreur de validation du code')
      setPromoApplied(null)
    } finally {
      setPromoValidating(false)
    }
  }

  const handleClearPromo = () => {
    setPromoApplied(null)
    setPromoInput('')
  }

  const handleAccept = async (token) => {
    setSubmitting(true);
    try {
      await httpRequest(`${API}/analyses/accept`, {
        method: 'POST',
        body: {
          analysis_token: token,
          phone: phone || null,
          message: message || null,
          promo_code: promoApplied?.code || null,
          plan: selectedPlan,
        },
      });
      toast.success('Demande confirmee ! Nous allons travailler sur votre dossier.');
      setPendingClaim(null);
      setPhone('');
      setMessage('');
      setPromoApplied(null);
      setPromoInput('');
      loadData();
    } catch (err) {
      toast.error(err instanceof HttpError ? err.message : "Erreur lors de la confirmation");
    } finally {
      setSubmitting(false);
    }
  };

  const handleStripeCheckout = async (token) => {
    setSubmitting(true);
    try {
      const data = await httpRequest(`${API}/payments/create-checkout-session`, {
        method: 'POST',
        body: {
          analysis_token: token,
          phone: phone || null,
          message: message || null,
          plan: selectedPlan,
        },
      });
      if (data?.url) {
        window.location.href = data.url;
        return;
      }
      toast.error('Réponse Stripe incomplète');
    } catch (err) {
      const extra = err instanceof HttpError ? err.message : err?.message || String(err);
      toast.error(
        err instanceof HttpError
          ? extra
          : `Impossible de démarrer le paiement — ${extra}`,
      );
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (val) => {
    if (val == null) return '0 EUR';
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(val);
  };

  const statusLabels = {
    none: { label: 'En attente', color: 'bg-[#2c2c2e] text-[#f5f5f7]/60' },
    pending: { label: 'A confirmer', color: 'bg-yellow-500/10 text-yellow-400 border-yellow-500/30' },
    accepted: { label: 'Confirmee', color: 'bg-green-500/10 text-green-400 border-green-500/30' },
    in_progress: { label: 'En cours', color: 'bg-blue-500/10 text-blue-400 border-blue-500/30' },
    completed: { label: 'Terminee', color: 'bg-green-500/10 text-green-400 border-green-500/30' },
    cancelled: { label: 'Annulee', color: 'bg-red-950/40 text-red-400 border-red-500/30' },
  };

  /** Analyse avec économie possible : paiement / confirmation pas encore finalisés (inclut anciennes lignes « none » après upload connecté). */
  const needsCommissionCheckout = (a) => {
    const ps = a.payment_status || 'none';
    if (['accepted', 'in_progress', 'completed', 'cancelled'].includes(ps)) return false;
    if (!a.has_savings) return false;
    return ps === 'pending' || ps === 'none';
  };

  const activePlanRow = pendingClaim
    ? planPricingFor(pendingClaim.result, selectedPlan)
    : { amount: 0, net_savings_for_client: 0 };
  const pendingCommission = pendingClaim ? Number(activePlanRow.amount ?? 0) : 0;
  const promoBypassesStripe = promoApplied?.discount_pct === 100;
  const useStripeCheckout =
    stripeCheckoutEnabled && pendingCommission >= stripeMinEur && !promoBypassesStripe;

  if (claiming) {
    return (
      <div className="min-h-screen bg-black">
        <Header />
        <div className="pt-32 flex flex-col items-center justify-center">
          <Loader2 size={32} className="text-green-400 animate-spin mb-4" />
          <p className="text-sm text-[#f5f5f7]/50">Association de votre analyse en cours...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black" data-testid="dashboard-page">
      <PostPaymentResiliationDialog
        open={postPaymentResiliationOpen}
        onOpenChange={setPostPaymentResiliationOpen}
      />
      <Header />
      <main className="pt-24 pb-16 max-w-5xl mx-auto px-6 md:px-12">
        <div className="mb-10">
          <h1 className="text-3xl sm:text-4xl tracking-tight font-bold text-[#f5f5f7] font-heading">
            Mon espace
          </h1>
          <p className="text-base text-[#f5f5f7]/50 mt-1">Gerez vos demandes de reduction de mensualite</p>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10" data-testid="stats-grid">
            <div className="bg-[#1c1c1e] border border-white/8 p-5 rounded-xl">
              <p className="text-xs uppercase tracking-[0.15em] font-semibold text-[#f5f5f7]/40 mb-1">Analyses</p>
              <p className="text-2xl font-black text-[#f5f5f7] font-heading">{stats.total_analyses}</p>
            </div>
            <div className="bg-[#1c1c1e] border border-white/8 p-5 rounded-xl">
              <p className="text-xs uppercase tracking-[0.15em] font-semibold text-green-400 mb-1">Économies brutes</p>
              <p className="text-2xl font-black savings-amount">{formatCurrency(stats.total_savings)}</p>
              <p className="text-[10px] text-[#f5f5f7]/35 mt-1.5">Cumul durée restante</p>
            </div>
            <div className="bg-[#1c1c1e] border border-white/8 p-5 rounded-xl">
              <p className="text-xs uppercase tracking-[0.15em] font-semibold text-[#f5f5f7]/40 mb-1">Nos frais (cumul)</p>
              <p className="text-2xl font-black commission-amount">{formatCurrency(stats.total_commission)}</p>
              <p className="text-[10px] text-[#f5f5f7]/35 mt-1.5">Honoraires prévus</p>
            </div>
            <div className="bg-[#1c1c1e] border border-white/8 p-5 rounded-xl">
              <p className="text-xs uppercase tracking-[0.15em] font-semibold text-[#f5f5f7]/40 mb-1">Gain net pour vous</p>
              <p className="text-2xl font-black text-[#f5f5f7] font-heading">{formatCurrency(stats.net_savings)}</p>
              <p className="text-[10px] text-[#f5f5f7]/35 mt-1.5">Après nos frais</p>
            </div>
          </div>
        )}

        {analyses.some((a) => needsCommissionCheckout(a)) && (
          <div
            className="mb-6 flex items-start gap-3 border border-orange-500/30 bg-orange-500/10 px-4 py-3 text-sm text-orange-400 rounded-xl"
            data-testid="pending-payment-banner"
          >
            <AlertCircle className="h-5 w-5 shrink-0 text-orange-400 mt-0.5" />
            <div>
              <p className="font-semibold">Paiement ou confirmation en attente</p>
              <p className="text-orange-400/90 mt-1">
                Ouvrez l’analyse concernée, puis <strong>Confirmer et payer</strong> pour choisir Essentiel ou Sérénité.
              </p>
            </div>
          </div>
        )}

        {/* Carte mandat / Stripe (claim récent ou ligne « Confirmer ») */}
        {pendingClaim && needsCommissionCheckout(pendingClaim) && (
          <div className="bg-green-500/10 border border-green-500/30 p-8 mb-8 animate-fade-up rounded-xl" data-testid="pending-claim-card">
            <div className="flex items-center gap-3 mb-4">
              <CheckCircle2 size={24} className="text-green-400" />
              <h2 className="text-xl font-bold tracking-tight font-heading text-[#f5f5f7]">
                Confirmez votre demande
              </h2>
            </div>
            <div className="text-sm text-[#f5f5f7]/60 mb-6 space-y-4">
              <p className="text-xs text-[#f5f5f7]/50 leading-relaxed">
                <span className="text-green-400 font-medium">Vert</span> : économies sur le loyer.{' '}
                <span className="text-orange-400 font-medium">Orange</span> : nos honoraires (paiement unique).
              </p>
              {(() => {
                const m = estimatedMonthlySavingEur(pendingClaim.result);
                const total = pendingClaim.result?.savings_analysis?.total_savings;
                const rem = Number(pendingClaim.result?.contract_details?.remaining_months);
                if (m == null && total == null) return null;
                return (
                  <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 text-[#f5f5f7]/70">
                    {m != null ? (
                      <span>
                        <span className="text-[#f5f5f7]/45">~</span>{' '}
                        <span className="font-bold text-green-400 tabular-nums">{formatCurrency(m)}</span>
                        <span className="text-[#f5f5f7]/45"> / mois</span>
                      </span>
                    ) : null}
                    {total != null ? (
                      <span>
                        <span className="text-[#f5f5f7]/45">Soit</span>{' '}
                        <span className="font-bold text-green-400 tabular-nums">{formatCurrency(total)}</span>
                        {Number.isFinite(rem) && rem > 0 ? (
                          <span className="text-[#f5f5f7]/45"> sur {rem} mois restants</span>
                        ) : (
                          <span className="text-[#f5f5f7]/45"> sur la durée restante</span>
                        )}
                      </span>
                    ) : null}
                  </div>
                );
              })()}
              <p className="text-[#f5f5f7]/50 text-xs">
                Confirmez pour lancer la prise en charge de votre dossier.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
              {PLAN_OPTIONS.map((opt) => {
                const row = planPricingFor(pendingClaim.result, opt.id);
                const active = selectedPlan === opt.id;
                return (
                  <button
                    key={opt.id}
                    type="button"
                    data-testid={`plan-${opt.id}-btn`}
                    onClick={() => setSelectedPlan(opt.id)}
                    className={`text-left rounded-xl border p-4 transition-[opacity,transform] duration-[180ms] ${
                      active
                        ? 'border-green-500/50 bg-green-500/10 ring-2 ring-green-500/30 scale-[1.01]'
                        : 'border-white/[0.08] bg-[#1c1c1e] hover:border-white/[0.14]'
                    }`}
                  >
                    <p className="text-sm font-bold text-[#f5f5f7] font-heading">{opt.title}</p>
                    <p className="text-xs text-[#f5f5f7]/45 mt-1">{opt.hint}</p>
                    <p className="text-[10px] uppercase tracking-[0.12em] text-orange-400/80 font-semibold mt-3 mb-1">
                      Honoraires (une fois)
                    </p>
                    <p className="text-lg font-black commission-amount">
                      {formatCurrency(row.amount)}
                    </p>
                  </button>
                );
              })}
            </div>

            <div className="bg-[#1c1c1e] border border-white/8 p-4 sm:p-5 mb-6 rounded-xl">
              <div className="grid grid-cols-3 gap-3 sm:gap-4 text-center">
                <div>
                  <p className="text-[11px] text-[#f5f5f7]/42 mb-1">Économie totale</p>
                  <p className="text-base sm:text-lg font-black text-green-400 font-heading tabular-nums">
                    {formatCurrency(pendingClaim.result?.savings_analysis?.total_savings)}
                  </p>
                </div>
                <div>
                  <p className="text-[11px] text-[#f5f5f7]/42 mb-1">
                    Frais ({selectedPlan === 'serenite' ? 'Sérénité' : 'Essentiel'})
                  </p>
                  {promoBypassesStripe ? (
                    <div>
                      <p className="text-base sm:text-lg font-black font-heading text-[#f5f5f7]/30 line-through tabular-nums">
                        {formatCurrency(activePlanRow.amount)}
                      </p>
                      <p className="text-sm font-bold text-green-400">0,00 EUR</p>
                    </div>
                  ) : (
                    <p className="text-base sm:text-lg font-black font-heading commission-amount tabular-nums">
                      {formatCurrency(activePlanRow.amount)}
                    </p>
                  )}
                </div>
                <div>
                  <p className="text-[11px] text-[#f5f5f7]/42 mb-1">Votre gain net</p>
                  <p className="text-base sm:text-lg font-black text-green-400 font-heading tabular-nums">
                    {promoBypassesStripe
                      ? formatCurrency(pendingClaim.result?.savings_analysis?.total_savings)
                      : formatCurrency(
                          activePlanRow.net_savings_for_client ??
                            pendingClaim.result?.commission?.net_savings_for_client,
                        )}
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              {!stripeCheckoutEnabled ? (
                <p className="text-xs text-orange-400 bg-orange-500/10 border border-orange-500/30 px-3 py-2 rounded-lg">
                  Paiement par carte non configuré sur ce serveur (variable <code className="text-[11px]">STRIPE_SECRET_KEY</code>).
                  Utilisez le bouton ci-dessous pour <strong>confirmer votre mandat sans carte</strong> (commission enregistrée côté équipe).
                </p>
              ) : null}
              {stripeCheckoutEnabled && !useStripeCheckout && pendingCommission > 0 && !promoBypassesStripe ? (
                <p className="text-xs text-[#f5f5f7]/60 bg-[#2c2c2e] border border-green-500/30 px-3 py-2 rounded-lg">
                  Montant de commission sous le minimum Stripe ({formatCurrency(stripeMinEur)}) : confirmation sans redirection carte.
                </p>
              ) : null}
              {/* Code promo */}
              <div>
                <Label className="text-sm font-medium text-[#f5f5f7]/80 mb-1.5 block">Code promo</Label>
                {promoApplied ? (
                  <div className="flex items-center gap-2 bg-green-500/10 border border-green-500/30 px-3 py-2.5 rounded-lg">
                    <Tag size={14} className="text-green-400 shrink-0" />
                    <span className="text-sm font-semibold text-green-400 flex-1">
                      {promoApplied.code} — commission offerte
                      {promoApplied.remaining != null ? ` (${promoApplied.remaining} restante${promoApplied.remaining > 1 ? 's' : ''})` : ''}
                    </span>
                    <button
                      onClick={handleClearPromo}
                      className="text-green-400/60 hover:text-green-400 transition-opacity duration-[180ms]"
                      data-testid="promo-clear-btn"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Tag size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#f5f5f7]/40" />
                      <Input
                        value={promoInput}
                        onChange={e => setPromoInput(e.target.value.toUpperCase())}
                        onKeyDown={e => e.key === 'Enter' && handleValidatePromo()}
                        placeholder="Entrez votre code"
                        className="pl-9 h-10 rounded-lg bg-[#2c2c2e] border-white/8 text-[#f5f5f7] placeholder:text-[#f5f5f7]/40 uppercase"
                        data-testid="promo-input"
                      />
                    </div>
                    <button
                      onClick={handleValidatePromo}
                      disabled={promoValidating || !promoInput.trim()}
                      className="h-10 px-4 text-sm font-semibold rounded-lg bg-[#2c2c2e] border border-white/[0.08] text-[#f5f5f7]/70 hover:text-[#f5f5f7] hover:border-white/20 disabled:opacity-40 transition-all duration-[180ms]"
                      data-testid="promo-apply-btn"
                    >
                      {promoValidating ? <Loader2 size={14} className="animate-spin" /> : 'Appliquer'}
                    </button>
                  </div>
                )}
              </div>
              <div>
                <Label className="text-sm font-medium text-[#f5f5f7]/80 mb-1.5 block">Telephone (optionnel)</Label>
                <div className="relative">
                  <Phone size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#f5f5f7]/40" />
                  <Input
                    value={phone}
                    onChange={e => setPhone(e.target.value)}
                    placeholder="06 12 34 56 78"
                    className="pl-9 h-10 rounded-lg bg-[#2c2c2e] border-white/8 text-[#f5f5f7] placeholder:text-[#f5f5f7]/40"
                    data-testid="accept-phone-input"
                  />
                </div>
              </div>
              <div>
                <Label className="text-sm font-medium text-[#f5f5f7]/80 mb-1.5 block">Message (optionnel)</Label>
                <div className="relative">
                  <MessageSquare size={14} className="absolute left-3 top-3 text-[#f5f5f7]/40" />
                  <textarea
                    value={message}
                    onChange={e => setMessage(e.target.value)}
                    placeholder="Precisions sur votre situation..."
                    className="w-full pl-9 p-3 text-sm bg-[#2c2c2e] border border-white/8 text-[#f5f5f7] placeholder:text-[#f5f5f7]/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0071e3] min-h-[80px] resize-none rounded-lg"
                    data-testid="accept-message-input"
                  />
                </div>
              </div>
              <button
                onClick={() =>
                  useStripeCheckout
                    ? handleStripeCheckout(pendingClaim.analysis_token)
                    : handleAccept(pendingClaim.analysis_token)
                }
                disabled={submitting}
                data-testid="confirm-accept-btn"
                className="w-full btn-green flex items-center justify-center gap-2 px-6 py-3 text-sm"
              >
                {submitting ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
                {promoBypassesStripe
                  ? 'Confirmer ma demande (gratuit)'
                  : useStripeCheckout
                    ? `Payer (Stripe) — ${formatCurrency(activePlanRow.amount)}`
                    : `Confirmer ma demande — ${formatCurrency(activePlanRow.amount)}`}
              </button>
              {useStripeCheckout ? (
                <p className="text-xs text-[#f5f5f7]/50 text-center">
                  Redirection sécurisée vers Stripe. Après paiement, votre demande sera créée automatiquement.
                </p>
              ) : null}
            </div>
          </div>
        )}

        {/* My analyses */}
        <div className="bg-[#1c1c1e] border border-white/8 p-6 rounded-xl" data-testid="my-analyses">
          <h2 className="text-lg font-bold tracking-tight mb-6 font-heading text-[#f5f5f7]">
            Mes demandes
          </h2>

          {analyses.length === 0 && !pendingClaim ? (
            <div className="text-center py-12">
              <BarChart3 size={40} className="mx-auto text-[#f5f5f7]/30 mb-4" />
              <p className="text-sm text-[#f5f5f7]/40 mb-2">Aucune demande pour le moment</p>
              <p className="text-xs text-[#f5f5f7]/30 mb-6">Uploadez un document sur la page d'accueil pour commencer</p>
              <button
                onClick={() => navigate('/')}
                data-testid="go-home-btn"
                className="btn-primary inline-flex items-center gap-2 px-6 py-3 text-sm"
              >
                Analyser un document
                <ArrowRight size={16} />
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {analyses.map((a) => {
                const r = a.result || {};
                const savings = r.savings_analysis || {};
                const ps = a.payment_status || 'none';
                const badgeKey =
                  ps === 'none' && a.has_savings ? 'pending' : ps;
                const st = statusLabels[badgeKey] || statusLabels.none;
                return (
                  <div
                    key={a.id}
                    className="p-4 border border-white/8 hover:bg-[#2c2c2e] transition-[opacity,transform] duration-[180ms] rounded-xl"
                    data-testid={`analysis-card-${a.id}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-3">
                        <p className="text-sm font-medium text-[#f5f5f7]">{a.document?.original_filename || 'Document'}</p>
                        <Badge className={`rounded-lg text-xs ${st.color}`}>{st.label}</Badge>
                      </div>
                      <p className="text-sm font-bold savings-amount">
                        {savings.total_savings ? `-${formatCurrency(savings.total_savings)}` : ''}
                      </p>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4 text-xs text-[#f5f5f7]/40">
                        <span>{r.vehicle?.make} {r.vehicle?.model}</span>
                        <span>{r.document_type}</span>
                        <span>{savings.savings_percentage != null ? `${savings.savings_percentage}%` : ''}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {needsCommissionCheckout(a) && (
                          <button
                            onClick={() => {
                              setPendingClaim({
                                result: r,
                                analysis_token: a.analysis_token,
                                has_savings: true,
                                payment_status: ps,
                              });
                              window.scrollTo({ top: 0, behavior: 'smooth' });
                            }}
                            className="text-xs font-semibold text-green-400 hover:text-green-300"
                            data-testid={`accept-btn-${a.id}`}
                          >
                            Confirmer et payer
                          </button>
                        )}
                        <button
                          onClick={() => navigate(`/analysis/${a.id}`)}
                          className="text-xs text-[#f5f5f7]/50 hover:text-[#f5f5f7]"
                          data-testid={`view-btn-${a.id}`}
                        >
                          Detail <ArrowRight size={12} className="inline" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* CTA to analyze more */}
        <div className="mt-8 text-center">
          <button
            onClick={() => navigate('/')}
            data-testid="analyze-more-btn"
            className="text-sm font-semibold text-green-400 hover:text-green-300 flex items-center gap-2 mx-auto"
          >
            Analyser un autre document
            <ArrowRight size={14} />
          </button>
        </div>
      </main>
    </div>
  );
}
